export function escapeCsv(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text =
    typeof value === "string" ? value : String(value);

  // Prevent spreadsheet formula injection when an exported CSV is opened in
  // Excel/Google Sheets. Numeric values remain numeric; only untrusted string
  // cells that begin with a formula trigger are escaped.
  const safeText =
    typeof value === "string" && /^[=+@\-\t]/.test(text)
      ? `'${text}`
      : text;

  if (
    safeText.includes(",") ||
    safeText.includes('"') ||
    safeText.includes("\n") ||
    safeText.includes("\r")
  ) {
    return `"${safeText.replace(/"/g, '""')}"`;
  }

  return safeText;
}

export function toCsv(
  headers: string[],
  rows: Array<Record<string, unknown>>,
): string {
  const lines = [
    headers.map(escapeCsv).join(","),
    ...rows.map((row) =>
      headers
        .map((header) => escapeCsv(row[header]))
        .join(","),
    ),
  ];

  return "\uFEFF" + lines.join("\r\n");
}

export function parseCsv(
  input: string,
): Array<Record<string, string>> {
  const text = input.replace(/^\uFEFF/, "");
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (quoted) {
      if (char === '"' && next === '"') {
        field += '"';
        index += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field.replace(/\r$/, ""));
    rows.push(row);
  }

  if (quoted) {
    throw new Error("CSV có dấu ngoặc kép chưa đóng.");
  }

  const nonEmpty = rows.filter((candidate) =>
    candidate.some((cell) => cell.trim().length > 0),
  );

  if (nonEmpty.length === 0) return [];

  const headers = nonEmpty[0]!.map((header) =>
    header.trim(),
  );

  if (headers.some((header) => !header)) {
    throw new Error("CSV có tiêu đề cột trống.");
  }

  const normalizedHeaders = headers.map((header) =>
    header.toLocaleLowerCase("vi"),
  );
  if (new Set(normalizedHeaders).size !== normalizedHeaders.length) {
    throw new Error("CSV có tiêu đề cột bị trùng.");
  }

  return nonEmpty.slice(1).map((cells) => {
    const record: Record<string, string> = {};
    headers.forEach((header, index) => {
      record[header] = cells[index] ?? "";
    });
    return record;
  });
}

export function downloadText(
  filename: string,
  content: string,
  mimeType = "text/plain;charset=utf-8",
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
